'use client'

import { type PropsWithChildren, useEffect } from 'react'
import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'
import { Toaster } from '@cocoonspace/ui/components/sonner'
import { TwicInstall } from '@twicpics/components/react'
import dynamic from 'next/dynamic'

import { InitSdks } from '~/components/init-sdks'
import { AuthLayout } from '~/components/layouts/auth-layout'
import { AppNavbar } from '~/components/nav-bar/app-nav-bar'
import { AuthDialog } from '~/domains/auth/components/auth-dialog'
import { BookingExpirationDialogManager } from '~/domains/booking/components/organisms/booking-expiration-dialog-manager'
import { CartSheetRoot } from '~/domains/cart/cart-sheet'
import { useHideFrontChat } from '~/hooks/use-hide-front-chat'
import { useLocale } from '~/hooks/use-locale.hook'
import { useSetErrorMap } from '~/lib/custom-zod-error-map'
import { envClient } from '~/lib/env'
import { Providers } from '~/providers/app-providers'

const MixPanelTracking = dynamic(() => import('~/lib/analytics'), {
	ssr: false,
})

export const AppShell = ({ children }: PropsWithChildren) => {
	const locale = useLocale()

	useSetErrorMap()

	useEffect(() => {
		dateTimeUtils.locale(locale)
	}, [locale])

	useHideFrontChat()

	return (
		<Providers>
			<InitSdks />

			<TwicInstall
				domain={envClient.NEXT_PUBLIC_TWICPICS_DOMAIN}
				path={envClient.NEXT_PUBLIC_TWICPICS_PATH}
				maxDPR={envClient.NEXT_PUBLIC_TWICPICS_MAX_DPR}
			/>

			<AuthLayout>
				<CartSheetRoot>
					<AppNavbar />
					<div id='main-content'>{children}</div>
					<BookingExpirationDialogManager />
				</CartSheetRoot>
			</AuthLayout>

			<AuthDialog />
			<MixPanelTracking />

			<Toaster
				position='top-right'
				theme='light'
			/>
		</Providers>
	)
}
